import React, { useState } from 'react';
import { Loader2, Send, CheckCircle2, AlertTriangle } from 'lucide-react';

/**
 * PushDiagramToComponentsButton — prominent action on the Diagrams tab
 * that aggregates every pin annotation on the Master Roof Plan, plus any
 * trace-tool segments, and pushes them into the Components tab. Idempotent:
 * any line previously tagged `import_source='roof_diagram'` /
 * `source='diagram'` is replaced; lines the operator typed by hand survive.
 *
 * Calls `POST /api/appraisals/{id}/roof-diagram-annotations/push-to-components`.
 */
export default function PushDiagramToComponentsButton({ api, appraisalId, onPushed }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { status, rollup, lines_created } or { error }

  const handlePush = async () => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(`/appraisals/${appraisalId}/roof-diagram-annotations/push-to-components`);
      setResult(data);
      if (data?.status === 'pushed' && typeof onPushed === 'function') onPushed(data);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message || 'Push failed' });
    } finally {
      setBusy(false);
    }
  };

  const summary = (() => {
    if (!result) return null;
    if (result.error) return { tone: 'err', text: result.error };
    if (result.status === 'noop') return { tone: 'warn', text: result.reason || 'No annotations to push yet.' };
    const rollup = result.rollup || {};
    const breakdown = Object.entries(rollup)
      .map(([ct, v]) => `${ct}: ${v.count} × ${v.length.toFixed(2)}m`)
      .join(' · ');
    return {
      tone: 'ok',
      text: `Pushed ${result.lines_created} component line${result.lines_created === 1 ? '' : 's'}${breakdown ? ' — ' + breakdown : ''}.`,
    };
  })();

  return (
    <div className="bg-gradient-to-r from-[#D35400]/5 to-emerald-50 rounded-md border border-[#D35400]/20 p-3 flex items-center gap-3" data-testid="push-diagram-to-components">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[#0F172A]">Push roof diagram → Components</p>
        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
          Sweeps every measurement pin + traced segment from the Master Roof Plan into the Components tab.
          Re-runs are safe — manually-typed lines stay, only diagram-sourced ones are refreshed.
        </p>
        {summary && (
          <div
            data-testid="push-diagram-result"
            className={`mt-2 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded ${
              summary.tone === 'ok'   ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200' :
              summary.tone === 'warn' ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-200' :
                                        'bg-rose-100 text-rose-800 ring-1 ring-rose-200'
            }`}
          >
            {summary.tone === 'ok' && <CheckCircle2 size={11} />}
            {summary.tone !== 'ok' && <AlertTriangle size={11} />}
            {summary.text}
          </div>
        )}
      </div>
      <button
        data-testid="push-diagram-to-components-btn"
        onClick={handlePush}
        disabled={busy || !appraisalId}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-white bg-[#D35400] hover:bg-[#A04000] disabled:opacity-50 disabled:cursor-not-allowed rounded-md shadow-sm transition-colors"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        {busy ? 'Syncing…' : 'Sync to Components'}
      </button>
    </div>
  );
}
